import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from "react";
import {
    Link,
    DataGrid,
    DataGridHeader,
    DataGridRow,
    DataGridHeaderCell,
    DataGridBody,
    DataGridCell,
    createTableColumn,
    TableCellLayout,
    Spinner,
    Skeleton,
    SkeletonItem
} from "@fluentui/react-components";
import {
    Folder24Regular,
    Document24Regular
} from "@fluentui/react-icons";

import { IContainerItem, IContainer } from "../../common/ContainerSchemas";
import { ContainerActionBar } from "../container-action-bar";
import { useNavigate, useParams, useLoaderData } from "react-router";
import { formatDate, getFolderPathRelativeToContainer } from "../../common/Utils";
import { JSX } from "react/jsx-runtime";
import SortableHeader from "../sortable-header/SortableHeader";
import { PaginationControls } from "../PaginationControls";
import "./ContainerBrowser.css";
import ErrorBoundary from "../../routes/error/ErrorBoundary";
import { ContainerItemActions } from "../container-item-actions";
import { PreviewProvider, PreviewContext } from "../container-item-preview";
import { DragDropArea } from "../drag-drop";
import { IPendingUpload } from "../container-action-bar/types/IPendingUpload";
import { useContainerData } from "../../hooks/useContainerData";
import { BlacklistedExtensions, BlacklistedExtensionsContext } from "../../common/BlacklistedExtensions";
import { ItemsApiProvider } from "../../providers/ItemsApiProvider";
import { isInShell } from "../../common/Shell";
import { useSharedStyles } from "../../shared.styles";
import { useContainerBrowserStyles } from "./ContainerBrowser.styles";

const itemsAPI = ItemsApiProvider.instance;

interface ItemNameDisplayProps {
    item: IContainerItem;
    containerId: string;
    onFolderClicked: (item: IContainerItem) => void;
    stopPropagation: (e: any) => void;
}

const ItemNameDisplay: React.FC<ItemNameDisplayProps> = ({
    item,
    containerId: containerId,
    onFolderClicked,
    stopPropagation
}) => {
    const previewContext = useContext(PreviewContext);

    if (item.type === 'folder') {
        return (
            <Link
                onClick={e => {
                    onFolderClicked(item);
                    stopPropagation(e);
                }}
            >
                {item.name}
            </Link>
        );
    }

    return (
        <Link
            onClick={e => {
                stopPropagation(e);
                previewContext?.openPreview(item, item.container_id ?? containerId ?? '');
            }}
        >
            {item.name}
        </Link>
    );
};

export const ContainerBrowser: React.FunctionComponent = () => {
    const navigate = useNavigate();
    const containers = useLoaderData() as IContainer[];
    const { containerId } = useParams<{ containerId: string }>();
    const inShell = React.useMemo(() => isInShell(), [])

    const {
        containerItems,
        folderPath,
        parentId,
        totalResults,
        isUILoading,
        isFolderLoading,
        isSearchLoading,
        error,
        searchQuery,
        searchResults,
        page,
        pageSize,
        isAllContainer,
        effectiveContainerId: effectiveContainerId,
        setFolderPath,
        setParentId,
        setSearchQuery,
        setSearchResults,
        setIsSearchLoading,
        setPage,
        setPageSize,
        setTotalResults,
        refreshFolderItems,
        onFolderClicked,
        onBreadcrumbClick,
        resetError
    } = useContainerData(containerId);
    const [activeSortColumn, setActiveSortColumn] = useState<string>("name");
    const [dragDropUploads, setDragDropUploads] = useState<Map<string, IPendingUpload>>(new Map());
    const sharedStyles = useSharedStyles();
    const browserStyles = useContainerBrowserStyles();

    const [sortDirections, setSortDirections] = useState<{ [key: string]: "asc" | "desc" }>({
        name: "asc",
        last_modified_timestamp: "asc",
        last_modified_by: "asc"
    });

    const getItemIcon = useCallback((containerItemLite: IContainerItem): JSX.Element => {
        if (containerItemLite.type === 'folder') {
            return <Folder24Regular className={sharedStyles.itemIcon} />;
        }

        // Use a standard document icon for files with transparent fill
        return <Document24Regular className={sharedStyles.itemIcon} />;
    }, [sharedStyles.itemIcon]);

    const renderFilesListContent = () => {
        if (isUILoading) {
            // Generate static unique IDs for skeleton items
            const skeletonIds = ['header', 'filename', 'modified', 'modifiedby', 'actions'];
            // Skeleton loading state for the data grid
            return (
                <div className={browserStyles.loadingContainer}>
                    <div className={browserStyles.skeletonWrapper}>
                        {skeletonIds.map((id) => (
                            <Skeleton
                                key={`skeleton-${id}`}
                                width="100%"
                                className={browserStyles.skeletonItem}
                            >
                                <SkeletonItem />
                            </Skeleton>
                        ))}
                    </div>
                </div>
            );
        } else if ((
            searchQuery && !isSearchLoading && searchResults.length === 0) || (containerItems.length === 0 && !isFolderLoading)
        ) {
            return (
                <p className={"noContentMessage"}>
                    No files or folders found.
                </p>
            );
        } else {
            return (
                <DataGrid
                    items={displayedItems}
                    columns={columns}
                    getRowId={(driveItem) => driveItem.id}
                    resizableColumns
                    columnSizingOptions={columnSizingOptions}
                    className={"dataGridFullWidth"}
                >
                    <DataGridHeader>
                        <DataGridRow className={"gridRow"}>
                            {({ renderHeaderCell }) => (
                                <DataGridHeaderCell>
                                    <b>{renderHeaderCell()}</b>
                                </DataGridHeaderCell>
                            )}
                        </DataGridRow>
                    </DataGridHeader>
                    {isFolderLoading && !isUILoading && (
                        <div className={"overlay"}>
                            <Spinner size="large" label="Loading..." />
                        </div>
                    )}
                    {!isFolderLoading && (
                        <DataGridBody<IContainerItem>>
                            {({ item, rowId }) => (
                                <DataGridRow<IContainerItem>
                                    key={rowId}
                                    className={"gridRow"}
                                >
                                    {({ renderCell }) => (
                                        <DataGridCell>{renderCell(item)}</DataGridCell>
                                    )}
                                </DataGridRow>
                            )}
                        </DataGridBody>
                    )}
                </DataGrid>
            );
        }
    };

    const stopPropagation = useCallback((e: any) => {
        if (e.stopPropagation) {
            e.stopPropagation();
        }
    }, []);

    // Drag and drop handlers
    const handleDragDropUploadProgress = (uploads: Map<string, IPendingUpload>) => {
        setDragDropUploads(new Map(uploads));
    };

    // Error handler reference that will be set by ContainerActionBar
    const errorHandlerRef = useRef<((message: string, details?: string) => void) | null>(null);

    // Function to be called by DragDropArea when errors occur
    const handleUploadError = (message: string, details?: string) => {
        // Use the ContainerActionBar's error handler if available
        if (errorHandlerRef.current) {
            errorHandlerRef.current(message, details);
        } else {
            // Fallback - log error if handler not set yet (shouldn't happen in normal operation)
            console.error('Upload error (no handler available):', message, details);
        }
    };

    const handleSearchQueryChange = (query: string) => {
        setSearchQuery(query);
        if (!query.trim().length) {
            // Reset pagination when the search query is cleared
            // Don't call refreshFolderItems() here as useEffect will handle it
            setTotalResults(0);
            setPage(1);
        }
    };

    const refresh = useCallback(() => {
        if (!isFolderLoading) {
            refreshFolderItems();
        }
    }, [isFolderLoading, refreshFolderItems]);

    const getItemName = useCallback((containerItemLite: IContainerItem): JSX.Element => {
        return (
            <ItemNameDisplay
                item={containerItemLite}
                containerId={containerId ?? ''}
                onFolderClicked={onFolderClicked}
                stopPropagation={stopPropagation}
            />
        );
    }, [containerId, onFolderClicked, stopPropagation]);

    const toggleSortDirection = useCallback((columnId: string) => {
        const newSortDirection = sortDirections[columnId] === "asc" ? "desc" : "asc";
        setSortDirections(prevSortDirections => ({
            ...prevSortDirections,
            [columnId]: newSortDirection
        }));
        setActiveSortColumn(columnId);
    }, [sortDirections]);

    const columns = useMemo(() => [
        createTableColumn<IContainerItem>({
            columnId: "driveItemName",
            renderHeaderCell: () => (
                <SortableHeader
                    title="Name"
                    sortDirection={sortDirections.name}
                    onClick={() => toggleSortDirection("name")}
                />
            ),
            renderCell: driveItem => (
                <TableCellLayout
                    media={getItemIcon(driveItem)}
                    title={driveItem.name}
                >
                    {getItemName(driveItem)}
                </TableCellLayout>
            )
        }),
        createTableColumn<IContainerItem>({
            columnId: "last_modified_timestamp",
            renderHeaderCell: () => {
                return (
                    <SortableHeader
                        title="Modified"
                        sortDirection={sortDirections.modified_at}
                        onClick={() => toggleSortDirection("modified_at")}
                    />
                );
            },
            renderCell: containerItemLite => {
                return <TableCellLayout>{formatDate(containerItemLite?.modified_at ?? '')}</TableCellLayout>;
            }
        }),
        createTableColumn<IContainerItem>({
            columnId: "last_modified_by",
            renderHeaderCell: () => {
                return (
                    <SortableHeader
                        title="Modified By"
                        sortDirection={sortDirections.modified_by}
                        onClick={() => toggleSortDirection("modified_by")}
                    />
                );
            },
            renderCell: containerItemLite => {
                return <TableCellLayout>{containerItemLite?.modified_by}</TableCellLayout>;
            }
        }),
        createTableColumn<IContainerItem>({
            columnId: "actions",
            renderHeaderCell: () => {
                return "";
            },
            renderCell: item => (
                <TableCellLayout className={browserStyles.actionsCell}>
                    <ContainerItemActions
                        item={item}
                        stopPropagation={stopPropagation}
                        containerId={item.container_id ?? containerId ?? ''}
                        onItemsUpdated={refresh}
                    />
                </TableCellLayout>
            )
        })
    ], [sortDirections, toggleSortDirection, containerId, getItemName, stopPropagation, refresh, browserStyles.actionsCell, getItemIcon]);

    const columnSizingOptions = useMemo(() => ({
        driveItemName: {
            minWidth: 250,
            defaultWidth: 600,      // Balanced width for filename column
            idealWidth: 600
        },
        last_modified_timestamp: {
            minWidth: 150,
            defaultWidth: 180,
            idealWidth: 180
        },
        last_modified_by: {
            minWidth: 150,
            defaultWidth: 180,
            idealWidth: 180
        },
        actions: {
            minWidth: 80,
            defaultWidth: 100,
            idealWidth: 100
        }
    }), []);



    const displayedItems = useMemo(() => {
        let baseItems: IContainerItem[];
        if (searchQuery) {
            baseItems = searchResults.length > 0 ? searchResults : [];
        } else {
            baseItems = containerItems;
        }
        const direction = sortDirections[activeSortColumn] ?? "asc";
        const multiplier = direction === "asc" ? 1 : -1;
        const sorted = [...baseItems].sort((a, b) => {
            if (activeSortColumn === "name") {
                return multiplier * a.name.localeCompare(b.name);
            }
            else if (activeSortColumn === "modified_at") {
                const aTime = new Date(a.modified_at ?? '').getTime();
                const bTime = new Date(b.modified_at ?? '').getTime();
                return multiplier * (aTime - bTime);
            }
            else if (activeSortColumn === "modified_by") {
                return multiplier * (a.modified_by ?? '').localeCompare(b.modified_by ?? '');
            }
            return 0;
        });
        return sorted;
    }, [searchQuery, searchResults, containerItems, sortDirections, activeSortColumn]);

    // Add a function to handle browser back button clicks
    const handleBackButtonClick = () => {
        // If inside a folder structure, return to container root
        setPage(1)
        if (folderPath.length > 0) {
            setFolderPath([]);
            setParentId("root");
        }
    };

    // Add click handler for back button on breadcrumb component
    const onBackButtonClick = () => {
        handleBackButtonClick();
    };

    // Handle browser back button
    useEffect(() => {
        const handleBrowserBackButton = (event: PopStateEvent) => {
            // Only handle if we're in a folder structure
            if (folderPath.length > 0) {
                // Prevent default navigation
                event.preventDefault();

                // Navigate to the root of current container
                setFolderPath([]);
                setParentId("root");

                // Update URL to stay on current container without adding a new history entry
                if (containerId) {
                    window.history.replaceState(null, '', `/containers/${containerId}`);
                }

                return false;
            }
        };

        // Use the popstate event to detect browser back button clicks
        window.addEventListener('popstate', handleBrowserBackButton);

        return () => {
            window.removeEventListener('popstate', handleBrowserBackButton);
        };
    }, [folderPath, containerId, setFolderPath, setParentId]);

    // Track folder navigation in browser history
    useEffect(() => {
        // Only add history entry when navigating into folders (not when returning to root)
        if (containerId && parentId !== "root" && folderPath.length > 0) {
            // Create a unique state object for this navigation point
            const state = {
                container: containerId,
                folderPath: folderPath.map(f => f.id),
                timestamp: new Date().getTime()
            };
            const prefix = inShell ? "documents/" : ""
            // Add a history entry that keeps us on the current container URL
            window.history.pushState(state, '', `${prefix}containers/${containerId}`);
        }
    }, [containerId, parentId, folderPath, inShell]);

    const [blacklistedExtensions, setBlacklistedExtensions] = useState<BlacklistedExtensions>([]);
    useEffect(() => {
        const fetchExtensionsBlacklist = async () => {
            try {
                const blacklistedExtensionsList: BlacklistedExtensions = await itemsAPI.getExtensionsBlacklist();
                setBlacklistedExtensions(blacklistedExtensionsList);
            } catch (error) {
                console.error('Failed to fetch extensions blacklist:', error);
            }
        };
        fetchExtensionsBlacklist();
    }, [])

    useEffect(() => {
        if (!containerId) return;
        setFolderPath([]);
        setParentId("root");
    }, [containerId, setFolderPath, setParentId]);

    if (error) {
        throw error;
    }

    return (
        <ErrorBoundary resetCallback={resetError}>
            <PreviewProvider>
                <BlacklistedExtensionsContext.Provider value={blacklistedExtensions}>
                    <>
                        {isFolderLoading && !isUILoading && (
                            <div className={"overlay"}>
                                <Spinner size="large" label="Loading..." />
                            </div>
                        )}
                        <div className="container-browser">
                            <div className="container-actions">
                                <ContainerActionBar
                                    containerId={effectiveContainerId}
                                    startingContainerId={containerId ?? ''}
                                    containerItems={containerItems}
                                    parentId={parentId}
                                    parentName={getFolderPathRelativeToContainer(folderPath)}
                                    searchQuery={searchQuery}
                                    onItemsUpdated={refresh}
                                    onSearchResults={setSearchResults}
                                    onSearchQueryChange={handleSearchQueryChange}
                                    isContainerRoot={parentId === "root"}
                                    page={page}
                                    pageSize={pageSize}
                                    setPage={setPage}
                                    setPageSize={setPageSize}
                                    setTotalResults={setTotalResults}
                                    setIsLoading={setIsSearchLoading}
                                    containers={containers}
                                    onContainerChange={(containerId) => {
                                        setFolderPath([]);
                                        navigate(`${inShell ? "/app/documents" : ""}/containers/${containerId}`);
                                        setParentId("root");
                                    }}
                                    folderPath={folderPath}
                                    onBreadcrumbClick={onBreadcrumbClick}
                                    onBackButtonClick={onBackButtonClick}
                                    isUILoading={isUILoading}
                                    dragDropUploads={dragDropUploads}
                                    errorHandlerRef={errorHandlerRef}
                                />
                            </div>
                            {!isAllContainer ? (
                                <DragDropArea
                                    containerId={effectiveContainerId}
                                    parentId={parentId}
                                    parentName={getFolderPathRelativeToContainer(folderPath)}
                                    onItemsUpdated={refresh}
                                    onUploadProgress={handleDragDropUploadProgress}
                                    onError={handleUploadError}
                                    disabled={isUILoading || isFolderLoading}
                                >
                                    <div className="files-list-container">
                                        {renderFilesListContent()}
                                    </div>
                                </DragDropArea>
                            ) : (
                                <div className="files-list-container">
                                    {renderFilesListContent()}
                                </div>
                            )}
                        </div>
                        {(isAllContainer || totalResults > 0) && (
                            <PaginationControls
                                page={page}
                                pageSize={pageSize}
                                totalResults={totalResults}
                                setPage={setPage}
                            />
                        )}
                    </>
                </BlacklistedExtensionsContext.Provider>
            </PreviewProvider>
        </ErrorBoundary>
    );
};

export default ContainerBrowser;